import { interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import { neon } from "../Materials";
import { BRAND } from "../theme";

/**
 * EP1 hero: a ring of "tenant" cubes slowly rotating. One cube is locked
 * (highlighted + a shackle). Optionally an outsider "query ray" shoots at the
 * locked cube and bounces off its wall (the RLS block).
 *
 * When showBlockedRay is used, set spin=0 so the locked cube stays at the front
 * (world +x), where the ray is aimed.
 */
const R = 2.6;

export const Vault: React.FC<{
  count?: number;
  lockIndex?: number;
  showBlockedRay?: boolean;
  spin?: number; // radians/sec
}> = ({ count = 6, lockIndex = 0, showBlockedRay = false, spin = 0.25 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const rot = (frame / fps) * spin;

  const cubes = Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2;
    return {
      x: Math.cos(a) * R,
      z: Math.sin(a) * R,
      locked: i === lockIndex,
      i,
    };
  });

  // Ray shoots toward the front cube (world +x) and bounces; impact pulses.
  const rayP = interpolate(frame % 90, [0, 30, 45, 90], [0, 1, 1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const rayHeadX = interpolate(rayP, [0, 1], [R + 2.4, R + 0.55]);
  const impact = interpolate(frame % 90, [28, 36, 50], [0, 1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <group>
      <group rotation={[0, rot, 0]}>
        {cubes.map((c) => (
          <group key={c.i} position={[c.x, 0, c.z]}>
            <mesh>
              <boxGeometry args={[1, 1, 1]} />
              <meshStandardMaterial
                {...neon(
                  c.locked ? BRAND.cyan : BRAND.violet,
                  c.locked ? 1.8 : 0.6,
                )}
              />
            </mesh>
            {c.locked && (
              <mesh position={[0, 0.82, 0]} rotation={[0, 0, 0]}>
                <torusGeometry args={[0.22, 0.06, 12, 24, Math.PI]} />
                <meshStandardMaterial {...neon(BRAND.amber, 1.8)} />
              </mesh>
            )}
          </group>
        ))}
      </group>

      {showBlockedRay && (
        <group>
          {/* incoming ray (thin emissive bar approaching the front cube) */}
          <mesh position={[(rayHeadX + (R + 3.2)) / 2, 0, 0]}>
            <boxGeometry args={[R + 3.2 - rayHeadX, 0.06, 0.06]} />
            <meshStandardMaterial {...neon(BRAND.magenta, 2)} />
          </mesh>
          {/* impact flash on the wall */}
          <mesh position={[R + 0.55, 0, 0]} scale={[impact, impact, impact]}>
            <sphereGeometry args={[0.5, 20, 20]} />
            <meshStandardMaterial {...neon(BRAND.magenta, 2.5)} />
          </mesh>
        </group>
      )}
    </group>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
